import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { formatAddress } from '@/common/utils/address';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { useCore } from '$api-clients/hooks';
import type { InteractionInformation } from '../../components/NeedsAttention/types';
import { LISTING_AGREEMENTS_NEEDS_ATTENTION_FILTERS } from '../useNeedsAttention/helpers';
import {
  buildClientNames,
  formatExpiringInMessage,
} from './useListingAgreements.helpers';

const PAGE_SIZE = 10;

export const useListingAgreements = ({ skip }: { skip: boolean }) => {
  const trackEvent = useTrackHomePageAnalytics();
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(1);
  const [interactions, setInteractions] = useState<
    Record<number, InteractionInformation[]>
  >({});

  useEffect(() => {
    if (skip) {
      // When the modal closes (skip becomes true), reset to page 1 so the next
      // open refetches from the first page rather than just the last one viewed.
      setCurrentPage(1);
    }
  }, [skip]);

  const { data, isLoading, error } = useCore(
    '/listings',
    skip
      ? null
      : {
          params: {
            query: {
              sort: 'listingEndDate:asc',
              facets: LISTING_AGREEMENTS_NEEDS_ATTENTION_FILTERS,
              needsAttention: true,
              includeContacts: true,
              offset: (currentPage - 1) * PAGE_SIZE,
              limit: PAGE_SIZE,
            },
          },
        },
  );

  useEffect(() => {
    if (error) {
      datadogRum.addError(error, {
        context: 'useListingAgreements: failed to fetch listings',
      });
    }
  }, [error]);

  useEffect(() => {
    if (!data) return;

    const formattedInteractions = data.results.map((listing) => ({
      subtitle: buildClientNames(listing.contacts ?? []),
      title: formatAddress(listing.property, { includeStreet2: true }),
      description: formatExpiringInMessage(listing.listingEndDate),
      id: listing.id,
      onClick: (numOrder: number) => {
        trackEvent(TrackHomePageEvent.NeedsAttentionModalInteractionClick, {
          interactionId: listing.id,
          numOrder,
        });
        void router.push(
          `/listings/${listing.id}/cover-sheet?utm_source=hp&utm_medium=nac`,
        );
      },
    }));

    setInteractions((prev) => ({
      ...prev,
      [currentPage]: formattedInteractions,
    }));
  }, [data, currentPage, router, trackEvent]);

  const handleLoadMore = () => {
    if (isLoading || !data?.next) return;
    setCurrentPage((prev) => prev + 1);
  };

  return {
    listingAgreementInteractions: Object.values(interactions).flat(),
    loading: isLoading,
    onLoadMore: handleLoadMore,
  };
};
